import type { RGBColor } from '@core/types'

/**
 * Parses a color string and returns RGBA components
 * Supports: #rgb, #rgba, #rrggbb, #rrggbbaa, rgb(), rgba()
 */
function parseColor(color: string): [number, number, number, number] {
    // Handle rgb/rgba with various separators
    const rgbMatch = color.match(/^rgb\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)\s*\)$/)

    if (rgbMatch) {
        const r = Number.parseInt(rgbMatch[1]!, 10)
        const g = Number.parseInt(rgbMatch[2]!, 10)
        const b = Number.parseInt(rgbMatch[3]!, 10)
        // Parse alpha: if % is present, divide by 100, otherwise take raw value. Default to 1.
        let a = rgbMatch[4] ? Number.parseFloat(rgbMatch[4]) : 1
        if (rgbMatch[5]) a /= 100

        return [r, g, b, a]
    }

    // Handle hex format
    let hex = color.startsWith('#') ? color.slice(1) : color

    // Expand shorthand (e.g. "03F" -> "0033FF")
    if (hex.length === 3 || hex.length === 4) {
        hex = hex.split('').map(char => char + char).join('')
    }

    const number = Number.parseInt(hex, 16)

    // Handle 8-digit hex (RRGGBBAA)
    if (hex.length === 8) {
        return [
            (number >> 24) & 0xFF,
            (number >> 16) & 0xFF,
            (number >> 8) & 0xFF,
            (number & 0xFF) / 255
        ]
    }

    // Handle 6-digit hex (RRGGBB)
    return [
        (number >> 16) & 0xFF,
        (number >> 8) & 0xFF,
        number & 0xFF,
        1
    ]
}

/**
 * Converts RGB (0-255) to HSL (0-360, 0-100, 0-100)
 */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255
    g /= 255
    b /= 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g: h = (b - r) / d + 2
                break
            case b: h = (r - g) / d + 4
                break
        }
        h *= 60
    }
    return [h, s * 100, l * 100]
}

/**
 * Converts HSL to RGB (0-255)
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    s /= 100
    l /= 100
    const k = (n: number) => (n + h / 30) % 12
    const a = s * Math.min(l, 1 - l)
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))

    return [
        Math.round(f(0) * 255),
        Math.round(f(8) * 255),
        Math.round(f(4) * 255)
    ]
}

/**
 * Lighten or darken a color by adjusting its Lightness.
 * This mimics Sass `color.adjust($color, $lightness: percentage)`
 *
 * @param color - color in hex format or rgb/rgba format
 * @param percentage - Amount to adjust lightness (e.g., 10 for +10%, -10 for -10%)
 * @param alpha - Optional alpha override (0-1). If omitted, preserves original alpha.
 */
export function shadeColor(color: string, percentage: number, alpha?: number): RGBColor {
    // 1. Parse current color
    // eslint-disable-next-line prefer-const
    let [r, g, b, currentAlpha] = parseColor(color)

    // 2. Convert to HSL
    // eslint-disable-next-line prefer-const
    let [h, s, l] = rgbToHsl(r, g, b)

    // 3. Adjust Lightness (Sass style: additive)
    l = l + percentage

    // 4. Clamp Lightness between 0 and 100
    l = Math.max(0, Math.min(100, l))

    // 5. Convert back to RGB
    ;[r, g, b] = hslToRgb(h, s, l)

    // 6. Determine final Alpha (Use override if provided, else use current)
    const finalAlpha = alpha !== undefined ? alpha : currentAlpha

    // 7. Return formatted string
    if (finalAlpha < 1) {
        return `rgb(${r} ${g} ${b} / ${+(finalAlpha * 100).toFixed(2)}%)`
    }
    return `rgb(${r} ${g} ${b})`
}
